import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Modal } from 'react-bootstrap';
import { isEmpty, uniqBy, uniq } from 'lodash';
import { unescapeHTML, titleize } from 'underscore.string';
import Alert from 'components/ui/Alert';
import AddButton from 'components/v2/buttons/AddButton';
import ReferenceNumberTypes from 'constants/ReferenceNumberTypes';
import DeleteButton from 'components/v2/buttons/DeleteButton';
import { showToast } from 'stores/ToastStore';
import TextField from 'components/v2/forms/TextField';
import { sluggify } from 'utils/localization/utils';
import { getReferenceNumberRequirements, getReferenceNumbersMasterList, } from 'utils/UserServiceAPIUtils';
import { STANDARD_REFERENCE_NUMBERS } from 'components/modules/master/constants';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import { determineReferenceNumberEntityTypes, mapUserSpecificReferenceNumbers, } from 'helpers/reference-numbers';
import { PICKUP, DELIVER } from 'constants/StopActions';
import uneditableReferenceNumbers from 'constants/UneditableReferenceNumbers';
import Tooltip from 'components/ui/Tooltip';
import CircleXIcon from 'components/v2/icons/CircleXIcon';
import CircleCheckIcon from 'components/v2/icons/CircleCheckIcon';
import SessionStore from 'stores/SessionStore';
import { isArrayEqual } from 'common/helpers/utils';
import { REFERENCE_NUMBER_MASTER_LIST_LIMIT } from 'constants/ListLimitConstants';
import ShowRequiredDocsBanner from 'components/shipment/maps/ShowRequiredDocsBanner';
const availableFields = ReferenceNumberTypes.filter((r) => r.editable);
const carrierBlacklist = [
    'house_bill_number',
    'mo_number',
    'master_bol',
    'shipment_id',
];
const carrierPageFields = (fields) => {
    return fields.filter((r) => carrierBlacklist.indexOf(r.name) === -1);
};
const entityTypeAllowedToDisableSaveBtn = ['shipment'];
function mapPropToState(props, state = {}) {
    const originalRefNums = props.reference_numbers || [];
    const stopFilterFunc = props.isStop ? (r) => r.stop_id : (r) => !r.stop_id; // filter stop reference numbers in or out
    let filtered = originalRefNums.filter(stopFilterFunc);
    if (props.isCarrier) {
        filtered = filtered.filter((ref) => carrierBlacklist.indexOf(ref.name) === -1);
    }
    const standardReferenceNumberMasterList = uniq(STANDARD_REFERENCE_NUMBERS.concat(state.standardReferenceNumberSlugs || []));
    const reference_numbers = filtered.map((r) => {
        const custom = !(standardReferenceNumberMasterList === null || standardReferenceNumberMasterList === void 0 ? void 0 : standardReferenceNumberMasterList.includes(r.name));
        const editable = !uneditableReferenceNumbers.includes(r.name);
        if (r.name === 'quote_id') {
            return r;
        }
        return Object.assign(Object.assign({}, r), { custom,
            editable });
    });
    return {
        reference_numbers,
        originalReferenceNumbers: originalRefNums,
    };
}
const checkIfReferenceNumbersUpdated = (initialReferenceNumbers = [], nextReferenceNumbers = []) => {
    const isRefsEqual = isArrayEqual(initialReferenceNumbers, nextReferenceNumbers);
    return !isRefsEqual;
};
class EditReferenceNumbersModal extends Component {
    constructor(props) {
        super(props);
        this.initializeModalState = (nextProps) => {
            this.setState((state) => {
                const error = state.error;
                return Object.assign(Object.assign({}, mapPropToState(nextProps, state)), { error });
            }, () => {
                this.setReferenceNumberRequirements();
                this.formatReferenceNumbersInState();
            });
        };
        this.fetchReferenceNumbersMasterList = async (nextProps) => {
            var _a;
            try {
                const response = await getReferenceNumbersMasterList(REFERENCE_NUMBER_MASTER_LIST_LIMIT);
                if (!this._isMounted)
                    return;
                const referenceList = ((_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.getReferenceNumbersQuery.reference_numbers) || [];
                const standardReferenceNumberSlugs = referenceList.map((item) => item.slug);
                const availableFields = referenceList
                    .filter((item) => !uneditableReferenceNumbers.includes(item.slug))
                    .map((item) => ({
                    name: item.slug,
                    localizationSlug: item.slug.toUpperCase(),
                    editable: true,
                }));
                this.setState({
                    referenceNumberMasterList: referenceList,
                    standardReferenceNumberSlugs,
                    availableFields,
                }, () => {
                    this.initializeModalState(nextProps);
                });
            }
            catch (_error) {
                if (!this._isMounted)
                    return;
                this.setState({
                    error: {
                        message: 'Failed to fetch reference numbers master list',
                    },
                });
            }
        };
        this.formatReferenceNumbersInState = () => {
            const { reference_numbers } = this.state;
            const sortedRefNums = reference_numbers.toSorted((a, b) => {
                if (!a.editable && b.editable) {
                    return -1;
                }
                else if (a.editable && !b.custom) {
                    return 1;
                }
                return 0;
            });
            this.setState({
                reference_numbers: sortedRefNums,
            });
        };
        this.handleAddRefNum = () => {
            const { reference_numbers: currentRefNums } = this.state;
            const reference_numbers = [
                ...currentRefNums,
                {
                    name: '',
                    value: '',
                    custom: false,
                    editable: true,
                },
            ];
            this.setState({
                reference_numbers,
            });
        };
        this.handleSave = () => {
            const error = this.validate();
            if (Object.keys(error).length === 0) {
                const { originalReferenceNumbers, reference_numbers } = this.state;
                // If this is not a stop, any stop reference numbers were filtered out.
                // Add them back in so they can be searched on (search doesn't look at stops).
                let stopReferenceNumbers = [];
                let updatedRefEvents = [];
                let newRefs = [...reference_numbers];
                if (!this.props.isStop) {
                    stopReferenceNumbers = originalReferenceNumbers.filter((r) => {
                        return !isEmpty(r.stop_id);
                    });
                }
                if (reference_numbers.length > originalReferenceNumbers.length) {
                    newRefs = reference_numbers.map((ref, i) => {
                        var _a;
                        if (!originalReferenceNumbers[i] ||
                            !((_a = originalReferenceNumbers[i]) === null || _a === void 0 ? void 0 : _a.value)) {
                            ref.isNew = true;
                        }
                        return ref;
                    });
                }
                updatedRefEvents = newRefs.filter((ref) => {
                    const refNum = originalReferenceNumbers.find((r) => r._id === ref._id);
                    if ((refNum === null || refNum === void 0 ? void 0 : refNum.name) !== ref.name || (refNum === null || refNum === void 0 ? void 0 : refNum.value) !== ref.value) {
                        return true;
                    }
                    return false;
                });
                this.props.onSave(reference_numbers.concat(stopReferenceNumbers), updatedRefEvents);
            }
        };
        this.handleCarrierSave = () => {
            const { reference_numbers, originalReferenceNumbers } = this.state;
            const carrierReferenceNumbers = originalReferenceNumbers
                .filter((ref) => carrierBlacklist.indexOf(ref.name) > -1)
                .concat(reference_numbers);
            const error = this.validate();
            if (Object.keys(error).length === 0) {
                this.props.onSave(carrierReferenceNumbers);
            }
        };
        this.validate = () => {
            const { reference_numbers } = this.state;
            const error = {};
            reference_numbers.forEach((refNum) => {
                const { name, value } = refNum;
                const filteredName = (name || '').trim();
                const filteredValue = (value || '').trim();
                const valid = filteredName.length > 0 && filteredValue.length > 0;
                if (!valid && !error.message) {
                    error.message = 'FILL_NAME_AND_VALUE_EACH_REFERENCE_NUM';
                }
            });
            this.setState({ error });
            return error;
        };
        this.state = Object.assign(Object.assign({}, mapPropToState(props)), { error: {}, originalReferenceNumbers: this.props.reference_numbers || [], requiredReferenceNumbers: null, availableFields: [], standardReferenceNumberSlugs: [], referenceNumberMasterList: [] });
    }
    UNSAFE_componentWillReceiveProps(nextProps) {
        const shouldFetch = (!this.props.show && nextProps.show) ||
            this.componentReceivedNewReferenceNumberProps(nextProps);
        if (shouldFetch) {
            if (this.state.referenceNumberMasterList.length === 0) {
                this.fetchReferenceNumbersMasterList(nextProps);
            }
            else {
                this.initializeModalState(nextProps);
            }
        }
    }
    componentDidMount() {
        this._isMounted = true;
    }
    componentWillUnmount() {
        this._isMounted = false;
    }
    componentReceivedNewReferenceNumberProps(nextProps) {
        if (!nextProps.reference_numbers) {
            return false;
        }
        return checkIfReferenceNumbersUpdated(this.props.reference_numbers, nextProps.reference_numbers);
    }
    setReferenceNumberRequirements() {
        this.checkShipperForReferenceNumberRequirements();
    }
    async checkShipperForReferenceNumberRequirements() {
        try {
            const { isIntermodalShipmentType, page, shipper, stop, getStopAction } = this.props;
            if (isIntermodalShipmentType && featureEnabled(CONFIGURED_FEATURES.SSR)) {
                let stopAction = null;
                if (getStopAction && !stop) {
                    stopAction = getStopAction();
                }
                else if (stop && !isEmpty(stop)) {
                    stopAction = stop.action === PICKUP ? PICKUP : DELIVER;
                }
                this.setIsLoadingShipperReferenceNumberRequirements(true);
                const requiredAt = determineReferenceNumberEntityTypes(page, stopAction);
                const shipperId = shipper._id;
                const requirements = await this.getShipperRequiredReferenceNumbers(shipperId, requiredAt);
                const mappedRequirements = mapUserSpecificReferenceNumbers(requirements);
                this.setState({
                    requiredReferenceNumbers: mappedRequirements || [],
                });
                this.setIsLoadingShipperReferenceNumberRequirements(false);
            }
            else {
                this.setState({
                    requiredReferenceNumbers: null,
                });
                this.setIsLoadingShipperReferenceNumberRequirements(false);
            }
        }
        catch (e) {
            console.log('Error checking user reference number requirements ', e.message);
            this.setIsLoadingShipperReferenceNumberRequirements(false);
        }
    }
    setIsLoadingShipperReferenceNumberRequirements(state) {
        this.setState({
            isFetchingSSR: state,
        });
    }
    async getShipperRequiredReferenceNumbers(id, requiredAt = null) {
        var _a;
        const { t } = this.props;
        try {
            const userResponse = await getReferenceNumberRequirements(id, requiredAt);
            if (!userResponse) {
                throw new Error('No response from API');
            }
            const referenceNumbers = (_a = userResponse === null || userResponse === void 0 ? void 0 : userResponse.data) === null || _a === void 0 ? void 0 : _a.getReferenceNumberRequirementsQuery;
            return referenceNumbers.map((refNum) => {
                return Object.assign(Object.assign({}, refNum), { reference_number: refNum.reference_number.slug });
            });
        }
        catch (err) {
            showToast(t('ERROR_FETCHING_DATA'), null, 'error');
            console.error('Error during fetchUserReferenceNumbers: ', err.message);
        }
    }
    getHandleValueChange(field, index) {
        return (value) => {
            const { reference_numbers: currentRefNums } = this.state;
            const refNum = Object.assign({}, currentRefNums[index]);
            refNum[field] = value;
            if (field === 'value') {
                refNum.optimized = value.toUpperCase();
            }
            const reference_numbers = [
                ...currentRefNums.slice(0, index),
                refNum,
                ...currentRefNums.slice(index + 1),
            ];
            this.setState({
                reference_numbers,
            });
        };
    }
    getHandleRemove(index) {
        return () => {
            const { reference_numbers: currentRefNums } = this.state;
            const reference_numbers = [
                ...currentRefNums.slice(0, index),
                ...currentRefNums.slice(index + 1),
            ];
            this.setState({
                reference_numbers,
            });
        };
    }
    getHandleReferenceNumberTypeChange(index) {
        return (e) => {
            const { reference_numbers: currentRefNums } = this.state;
            const targetRefNum = Object.assign({}, currentRefNums[index]);
            if (e.target.value === 'custom') {
                targetRefNum.name = '';
                targetRefNum.custom = true;
            }
            else {
                targetRefNum.name = e.target.value;
                targetRefNum.custom = false;
            }
            this.setState({
                reference_numbers: [
                    ...currentRefNums.slice(0, index),
                    targetRefNum,
                    ...currentRefNums.slice(index + 1),
                ],
            });
        };
    }
    _isUserReferenceNumbersAdmin() {
        return SessionStore.isReferenceNumbersAdmin();
    }
    renderErrors() {
        const { error } = this.state;
        const { t } = this.props;
        const errorKeys = Object.keys(error);
        const hasErrors = errorKeys.length > 0;
        if (hasErrors) {
            return (React.createElement(Alert, { dataTestId: "Alert-9db71913", type: "error", className: "mb-4" },
                React.createElement("ul", { "data-testid": "ul-211c45ef" }, errorKeys.map((key, index) => (React.createElement("li", { "data-testid": `li-fac6d4e8-${index}` }, t(error[key])))))));
        }
        return null;
    }
    renderNameDropdown(refNum, index) {
        if (!refNum.editable || refNum.custom) {
            return null;
        }
        let shipmentPageFields = '';
        const refValues = ['isa', 'truck_order', 'VRID'];
        const { isIntermodalShipmentType, t } = this.props;
        if (isIntermodalShipmentType) {
            if (this.state.requiredReferenceNumbers) {
                const existingAvailableFields = this.state.availableFields.filter((f) => {
                    return !!this.state.reference_numbers.find((r) => r.name.toUpperCase() === f.name.toUpperCase());
                });
                shipmentPageFields = uniqBy([
                    ...existingAvailableFields,
                    ...this.state.requiredReferenceNumbers.filter((r) => r.editable),
                ], 'name');
            }
            else {
                shipmentPageFields = availableFields;
            }
        }
        else {
            shipmentPageFields = availableFields.filter((field) => !refValues.includes(field.name));
        }
        let displayedFields = shipmentPageFields;
        if (this.props.isCarrier) {
            displayedFields = carrierPageFields(shipmentPageFields);
        }
        displayedFields = displayedFields.sort((a, b) => a.name.localeCompare(b.name));
        return (!this.state.isFetchingSSR && (React.createElement("select", { "data-testid": "select-e76ae925", className: "w-full form-select", value: refNum.name, placeholder: t('SELECT_REFERENCE_NUM_TYPE'), onChange: this.getHandleReferenceNumberTypeChange(index) },
            React.createElement("option", { "data-testid": "option-b5a1d719", key: "default-0", value: "" }, t('SELECT_A_TYPE')),
            displayedFields.map((standardRef, index) => {
                return (React.createElement("option", { "data-testid": `option-0520e435-${index}`, key: standardRef.name, value: standardRef.name }, t(standardRef === null || standardRef === void 0 ? void 0 : standardRef.localizationSlug)));
            }),
            React.createElement("option", { "data-testid": "option-c5d19476", key: "custom-0", value: "custom" }, titleize(t('CUSTOM'))))));
    }
    renderCustomName(refNum, index) {
        const { t } = this.props;
        if (refNum.editable && refNum.custom) {
            return (React.createElement(TextField, { dataTestId: "TextField-6084561a", type: "text", value: unescapeHTML(refNum.name), placeholder: t('REF_#_NAME'), onChange: this.getHandleValueChange('name', index) }));
        }
        return null;
    }
    renderReadonlyName(refNum) {
        const { t } = this.props;
        if (!refNum.editable) {
            return (React.createElement(TextField, { dataTestId: "TextField-4bc78050", type: "text", value: t(sluggify(refNum.name === 'tracking_id' ? 'tracking' : refNum.name)), readOnly: true, placeholder: t('REF_#_NAME'), onChange: () => { } }));
        }
        return null;
    }
    renderEditableValue(refNum, i) {
        const { t } = this.props;
        if (refNum.editable) {
            return (React.createElement(TextField, { dataTestId: "TextField-31cf1f5f", type: "text", value: unescapeHTML(refNum.value), placeholder: t('REF_#_VALUE'), onChange: this.getHandleValueChange('value', i) }));
        }
        return null;
    }
    renderReadonlyValue(refNum) {
        const { t } = this.props;
        if (!refNum.editable) {
            return (React.createElement(TextField, { dataTestId: "TextField-ef44809c", type: "text", value: unescapeHTML(refNum.value), readOnly: true, placeholder: t('REF_#_VALUE'), onChange: () => { } }));
        }
        return null;
    }
    renderEditableIcon(refNum) {
        const { t } = this.props;
        if (refNum.editable) {
            return (React.createElement(Tooltip, { dataTestId: "Tooltip-794504a6", id: refNum._id, content: t('USER_REFERENCE_NUMBER_INFO'), position: "top" },
                React.createElement(CircleCheckIcon, { className: "text-green-cm h-5 w-5 mt-2" })));
        }
        return (React.createElement(Tooltip, { dataTestId: "Tooltip-6d3f5564", id: refNum._id, content: t('SYSTEM_REFERENCE_NUMBER_INFO'), position: "top" },
            React.createElement(CircleXIcon, { className: "text-red-400 h-5 w-5 mt-2" })));
    }
    renderReferenceNumbers() {
        return this.state.reference_numbers.map((refNum, i) => (React.createElement("tr", { "data-testid": `tr-002ffa6c-${i}`, key: i },
            React.createElement("td", { "data-testid": `td-fadc50d1-${i}` },
                this.renderNameDropdown(refNum, i),
                this.renderReadonlyName(refNum),
                this.renderCustomName(refNum, i)),
            React.createElement("td", { "data-testid": `td-98492b55-${i}` },
                this.renderEditableValue(refNum, i),
                this.renderReadonlyValue(refNum)),
            React.createElement("td", { "data-testid": `td-a9058688-${i}` }, this.renderEditableIcon(refNum)),
            React.createElement("td", { "data-testid": `td-4598fc0b-${i}` }, (refNum.editable || this._isUserReferenceNumbersAdmin()) &&
                !this.props.isCarrier && (React.createElement(DeleteButton, { dataTestId: `DeleteButton-d6113c1a-${i}`, onClick: this.getHandleRemove(i) }))))));
    }
    renderBody() {
        const { t } = this.props;
        return (React.createElement("div", { "data-testid": "div-c428be74", className: "overflow-x-auto" },
            React.createElement("table", { "data-testid": "table-a81b50f0", className: "default-table" },
                React.createElement("thead", { "data-testid": "thead-e3d70483" },
                    React.createElement("tr", { "data-testid": "tr-54ff52b1" },
                        React.createElement("th", { "data-testid": "th-58a63900" }, t('NAME')),
                        React.createElement("th", { "data-testid": "th-aa0a3476" }, t('VALUE')),
                        React.createElement("th", { "data-testid": "th-77782b99" }, `${t('EDITABLE')}?`),
                        React.createElement("th", { "data-testid": "th-3e92348c" },
                            React.createElement(AddButton, { dataTestId: "AddButton-cf3bcc37", onClick: this.handleAddRefNum })))),
                React.createElement("tbody", { "data-testid": "tbody-557c4652" }, this.renderReferenceNumbers()))));
    }
    render() {
        const body = this.props.show ? this.renderBody() : null;
        const { title, t, requiredReferenceNumbers } = this.props;
        return (React.createElement(Modal, { show: this.props.show, enforceFocus: false },
            React.createElement(Modal.Header, { "data-testid": "undefined-20b6bd65" },
                React.createElement("h4", { "data-testid": "h4-d94b4542" }, t(title))),
            React.createElement(Modal.Body, { "data-testid": "undefined-5085b83e" },
                this.renderErrors(),
                React.createElement(ShowRequiredDocsBanner, { docsList: requiredReferenceNumbers }),
                body),
            React.createElement(Modal.Footer, { "data-testid": "undefined-03b77fe1" },
                React.createElement("button", { "data-testid": "button-2d11548c", className: "button button-green", disabled: this.props.entityType &&
                        entityTypeAllowedToDisableSaveBtn.includes(this.props.entityType)
                        ? false
                        : this.state.reference_numbers.length === 0, onClick: this.props.isCarrier ? this.handleCarrierSave : this.handleSave }, titleize(t('SAVE'))),
                React.createElement("button", { "data-testid": "button-acc98c23", onClick: () => this.props.onClose(), className: "button button-secondary" }, t('CANCEL')))));
    }
}
EditReferenceNumbersModal.propTypes = {
    show: PropTypes.bool,
    shipper: PropTypes.object,
    title: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    isCarrier: PropTypes.bool,
    isStop: PropTypes.bool,
    page: PropTypes.string,
    reference_numbers: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
    })),
    isIntermodalShipmentType: PropTypes.bool,
    entityType: PropTypes.string,
};
EditReferenceNumbersModal.defaultProps = {
    show: false,
    title: 'EDIT_REFERENCE_NUMBERS',
    isCarrier: false,
};
export default withTranslation()(EditReferenceNumbersModal);
